import React, { useState } from 'react';
import {
  Row, Col, Card, Form, Button, Spinner, Toast, ToastContainer, Alert,
  Badge
} from 'react-bootstrap';
import { FaFileDownload, FaEye } from 'react-icons/fa';
import axios from 'axios';
import Select from 'react-select';

const UaePolicyGeneratorTab = ({ userData, showNotification, region, refreshActivities,logToAccessLogs }) => {
  const [form, setForm] = useState({
    orgName: '',
    industry: '',
    location: '',
    activities: [],
    policyTypes: [],
    dpoContactDetails: '',
    legalBasis: []
  });
  const [loading, setLoading] = useState(false);
  const [generatedPolicies, setGeneratedPolicies] = useState([]);

  const UAE_ACTIVITY_OPTIONS = [
    { value: 'Customer Relationship Management', label: 'Customer Relationship Management' },
    { value: 'Marketing & Communications', label: 'Marketing & Communications' },
    { value: 'Employee Data Processing', label: 'Employee Data Processing' },
    { value: 'Financial Transactions & Banking', label: 'Financial Transactions & Banking' },
    { value: 'Website Analytics & Digital Tracking', label: 'Website Analytics & Digital Tracking' },
    { value: 'Healthcare Data Processing', label: 'Healthcare Data Processing' },
    { value: 'Video Surveillance & CCTV', label: 'Video Surveillance & CCTV' },
    { value: 'E-commerce & Online Services', label: 'E-commerce & Online Services' },
    { value: 'Automated Decision Making & Profiling', label: 'Automated Decision Making & Profiling' },
    { value: 'Cross-Border Data Transfers', label: 'Cross-Border Data Transfers' },
    { value: 'Government Services & Public Sector', label: 'Government Services & Public Sector' },
    { value: 'Real Estate & Property Management', label: 'Real Estate & Property Management' }
  ];

  const UAE_LEGAL_BASIS_OPTIONS = [
    { value: 'Explicit Consent', label: 'Explicit Consent of Data Subject' },
    { value: 'Contractual Necessity', label: 'Contractual Necessity' },
    { value: 'Legal Obligation', label: 'Legal Obligation under UAE Law' },
    { value: 'Vital Interests', label: 'Vital Interests Protection' },
    { value: 'Public Interest', label: 'Public Interest Task' },
    { value: 'Legitimate Interest', label: 'Legitimate Interest' },
    { value: 'Employment Purpose', label: 'Employment Purpose' },
    { value: 'Medical Treatment', label: 'Medical Treatment & Health Services' }
  ];

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };

  const handleReset = () => {
    setForm({
      orgName: '',
      industry: '',
      location: '',
      activities: [],
      policyTypes: [],
      dpoContactDetails: '',
      legalBasis: []
    });
    setGeneratedPolicies([]);
  };

  const handlePreview = async () => {
    if (form.policyTypes.length === 0) {
      showNotification('Select at least one policy to preview.', 'warning');
      return;
    }

    const previewType = form.policyTypes[0];
    const typeSlug = previewType.toLowerCase().replace(/\s+/g, '-');
    
    try {
      const res = await axios.get(`https://jqbs9k66j6.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/policy-generator/sample/${typeSlug}`, {
        headers,
        responseType: 'blob'
      });
      
      const file = new Blob([res.data], { type: 'text/plain' });
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
      
      await logActivity(`Previewed UAE ${previewType} sample policy`, "info");
    } catch (err) {
      console.error('Error previewing UAE policy:', err);
      showNotification('Error previewing policy', 'danger');
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setForm((prevForm) => ({
      ...prevForm,
      policyTypes: checked
        ? [...prevForm.policyTypes, value]
        : prevForm.policyTypes.filter((p) => p !== value),
    }));
  };

const downloadPolicy = async (policy) => {
  try {
    const res = await axios.get(
      `https://qwqzy0a3vj.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/policy-generator/download/${policy.id}`,
      {
        headers,
        responseType: 'blob' // ✅ ensures binary file download
      }
    );

    // ✅ Create a Blob with the correct MIME type for Word documents (.docx)
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // ✅ Change extension from .txt → .docx
    link.setAttribute('download', `UAE_PDPL_${policy.name.replace(/\s+/g, '_')}.docx`);

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    await logActivity(
      `Downloaded generated UAE ${policy.name} for ${form.orgName} (${form.industry})`,
      "info"
    );
     logToAccessLogs?.({
        action:      'Policy Downloaded',
        severity:    'info',
        description: `Downloaded UAE policy: ${policy.name} for ${form.orgName}`,});
  } catch (error) {
    console.error('Error downloading UAE policy:', error);
    showNotification('Error downloading policy', 'danger');
  }
};


  const handleGenerate = async () => {
    if (!form.orgName || !form.industry || !form.location || form.policyTypes.length === 0) {
      showNotification('Please fill all required fields.', 'danger');
      return;
    }

    setLoading(true);
    try {
      const payload = { ...form };
      
      const res = await axios.post(
        'https://p0sn032zne.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/policy-generator/create',
        payload,
        { headers }
      );
      
      setGeneratedPolicies(res.data.generated || []);
      showNotification('UAE PDPL-compliant policies generated successfully!');
      await logActivity(`Generated ${form.policyTypes.length} UAE PDPL policies for ${form.orgName} in ${form.industry} sector: ${form.policyTypes.join(', ')}`, "success");
      logToAccessLogs?.({
        action:      'Policy Generated',
        severity:    'success',
        description: `Generated ${res.data.generated?.length ?? 0} GDPR policies for ${form.orgName} (${form.industry})`,});
    } catch (err) {
      console.error("Error generating UAE policies:", err);
      showNotification('Error generating UAE policies', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const logActivity = async (message, badge = "info") => {
    try {
      const payload = {
        action: badge,
        module: "UAE Policy Generator",
        details: message
      };
      
      const response = await axios.post(
        "https://6x7bayihnj.execute-api.ap-southeast-2.amazonaws.com/prod/api/uae/activity-log/save",
        payload,     
        { headers }
      );
      
      if (refreshActivities) {
        await refreshActivities();
      }
      
    } catch (err) {
      console.error('❌ Error logging UAE policy generator activity:', err);
      
      if (err.response) {
        console.error('🔥 Server Error Details:');
        console.error('Status:', err.response.status);
        console.error('Data:', err.response.data);
      }
      
      console.warn("Failed to log UAE activity:", err.message);
    }
  };

  return (
    <Card>
      <Card.Header>
        <h5>Generate UAE PDPL-Compliant Policies</h5>
      </Card.Header>
      <Card.Body>
        <Alert variant="info">
          <strong>UAE Personal Data Protection Law (PDPL) Policy Generator:</strong> This tool generates policies compliant with 
          UAE Federal Decree-Law No. 45 of 2021. All policies include required PDPL elements such as legal basis, 
          data subject rights, processing purposes, and data protection officer information where applicable.
        </Alert>
        
        <Form>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Organization Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="orgName"
                  value={form.orgName}
                  onChange={handleChange}
                  placeholder="Your organization name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Industry Sector *</Form.Label>
                <Form.Select name="industry" value={form.industry} onChange={handleChange}>
                  <option value="">Select Industry</option>
                  <option value="Banking & Financial Services">Banking & Financial Services</option>
                  <option value="Healthcare & Medical Services">Healthcare & Medical Services</option>
                  <option value="E-commerce & Retail">E-commerce & Retail</option>
                  <option value="Education & Training">Education & Training</option>
                  <option value="Technology & Software">Technology & Software</option>
                  <option value="Manufacturing & Industrial">Manufacturing & Industrial</option>
                  <option value="Telecommunications & Media">Telecommunications & Media</option>
                  <option value="Tourism & Hospitality">Tourism & Hospitality</option>
                  <option value="Transportation & Logistics">Transportation & Logistics</option>
                  <option value="Real Estate & Construction">Real Estate & Construction</option>
                  <option value="Professional Services">Professional Services</option>
                  <option value="Oil & Gas">Oil & Gas</option>
                  <option value="Government & Public Sector">Government & Public Sector</option>
                  <option value="Aviation & Aerospace">Aviation & Aerospace</option>
                  <option value="Free Zone Operations">Free Zone Operations</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          <Form.Group className="mb-3">
            <Form.Label>Primary Business Location *</Form.Label>
            <Form.Select name="location" value={form.location} onChange={handleChange}>
              <option value="">Select Location</option>
              <option value="UAE Mainland">UAE Mainland</option>
              <option value="Dubai International Financial Centre (DIFC)">Dubai International Financial Centre (DIFC)</option>
              <option value="Abu Dhabi Global Market (ADGM)">Abu Dhabi Global Market (ADGM)</option>
              <option value="Dubai Multi Commodities Centre (DMCC)">Dubai Multi Commodities Centre (DMCC)</option>
              <option value="Dubai Internet City (DIC)">Dubai Internet City (DIC)</option>
              <option value="Abu Dhabi Free Zones">Abu Dhabi Free Zones</option>
              <option value="Sharjah Free Zones">Sharjah Free Zones</option>
              <option value="UAE + GCC Operations">UAE + GCC Operations</option>
              <option value="UAE + International Operations">UAE + International Operations</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Personal Data Processing Activities</Form.Label>
            <Select
              isMulti
              options={UAE_ACTIVITY_OPTIONS}
              value={UAE_ACTIVITY_OPTIONS.filter(opt => form.activities.includes(opt.value))}
              onChange={(selected) => {
                setForm({ ...form, activities: selected.map(s => s.value) });
              }}
              placeholder="Select all applicable processing activities..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Legal Basis for Processing</Form.Label>
            <Select
              isMulti
              options={UAE_LEGAL_BASIS_OPTIONS}
              value={UAE_LEGAL_BASIS_OPTIONS.filter(opt => form.legalBasis.includes(opt.value))}
              onChange={(selected) => {
                setForm({ ...form, legalBasis: selected.map(s => s.value) });
              }}
              placeholder="Select applicable legal basis under UAE PDPL..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Data Protection Officer / Privacy Contact</Form.Label>
            <Form.Control
              type="text"
              name="dpoContactDetails"
              value={form.dpoContactDetails}
              onChange={handleChange}
              placeholder="e.g., privacy@yourcompany.ae or +971-X-XXXXXXX"
            />
            <Form.Text className="text-muted">
              Required for high-risk processing activities or organizations processing large volumes of personal data
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Policy Types to Generate *</Form.Label>
            <Row>
              {[
                'UAE Privacy Notice',
                'Cookie & Digital Tracking Policy',
                'Data Retention & Deletion Policy',
                'Consent Management Policy',
                'Data Subject Rights Policy',
                'Personal Data Breach Response Policy',
                'Data Processing Agreement (DPA)',
                'Cross-Border Transfer Policy',
                'Employee Privacy Notice',
                'CCTV & Surveillance Policy',
                'Website Terms & Privacy Policy',
                'Marketing Communications Policy'
              ].map((type) => (
                <Col md={6} key={type}>
                  <Form.Check
                    type="checkbox"
                    id={type}
                    label={type}
                    value={type}
                    checked={form.policyTypes.includes(type)}
                    onChange={handleCheckboxChange}
                  />
                </Col>
              ))}
            </Row>
          </Form.Group>

          <Alert variant="warning" className="mb-3">
            <strong>UAE PDPL Compliance Note:</strong> Generated policies will include mandatory UAE requirements including:
            <ul className="mb-0 mt-0">
              <li>Arabic translation requirements for consumer-facing policies</li>
              <li>UAE-specific data subject rights (access, rectification, deletion, portability)</li>
              <li>Cross-border transfer safeguards and legal mechanisms</li>
              <li>Local data residency considerations</li>
              <li>Breach notification procedures (72-hour rule to authorities)</li>
            </ul>
          </Alert>

          <div className="d-flex gap-2">
            <Button variant="primary" onClick={handleGenerate} disabled={loading}>
              {loading && <Spinner as="span" animation="border" size="sm" className="me-2" />}
              Generate UAE PDPL Policies
            </Button>
            <Button variant="secondary" onClick={handleReset}>Reset</Button>
            <Button 
              variant="outline-info" 
              onClick={handlePreview}
              disabled={form.policyTypes.length === 0}
            >
              <FaEye /> Preview Sample
            </Button>
          </div>
        </Form>

        <hr />

        <div>
          <h6>Generated UAE PDPL-Compliant Policies</h6>
          {generatedPolicies.length === 0 ? (
            <Alert variant="secondary">No policies generated yet. Select your requirements above and click "Generate UAE PDPL Policies".</Alert>
          ) : (
            <>
              <Alert variant="success" className="mb-3">
                <strong>Generated Successfully:</strong> Your policies have been generated in compliance with UAE Personal Data Protection Law. 
                Each policy includes UAE-specific requirements and can be customized further for your organization.
              </Alert>
              <Row>
                {generatedPolicies.map((policy, idx) => (
                  <Col md={6} key={idx} className="mb-3">
                    <Card>
                      <Card.Body>
                        <Card.Title>{policy.name}</Card.Title>
                        <Card.Text className="text-muted">{policy.description}</Card.Text>
                        <Badge bg="info" className="mb-2">UAE PDPL Compliant</Badge>
                        <br />
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => downloadPolicy(policy)}
                        >
                          <FaFileDownload /> Download
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default UaePolicyGeneratorTab;
